import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { Flex, Grid, VerticalDivider } from "src/elements";
import { Metric } from "../Metric/Metric";
import { Tooltip } from "../Tooltip/Tooltip";
import { PrimitiveAtom, useAtom } from "jotai";
import { useMemo } from "react";
import { useTranslation } from "src/i18n";
import { pogAestheticsScore } from "src/pages/CdtStudio/domain/pogAesthetics";
import { ExpectedSales, pogExpectedSales } from "src/pages/CdtStudio/domain/pogExpectedSales";
import { METRIC_IDS, METRIC_TRANSLATION_KEYS, MetricId } from "./metricDescriptions";

const TOOLTIP_WIDTH = "320px";

const AMOUNT_FORMAT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const formatExpectedSales = (expectedSales: ExpectedSales | null) =>
  !expectedSales || expectedSales.isPlaceholderData || !expectedSales.skuCount
    ? null
    : AMOUNT_FORMAT.format(expectedSales.total);

const TILE_COLUMNS = METRIC_IDS.map(() => "minmax(0, 1fr)").join(" auto ");

const MetricTile = ({
  id,
  percent,
  value,
  size,
  isLoading,
  emptyReason,
}: {
  id: MetricId;
  percent?: number | null;
  value?: string | null;
  size?: "default" | "small" | "big";
  isLoading?: boolean;
  emptyReason?: string | null;
}) => {
  const { t } = useTranslation("common");
  const translationKeys = METRIC_TRANSLATION_KEYS[id];
  const title = t(translationKeys.title);

  return (
    <Flex direction="column" width="100%" role="group" aria-label={title} tabIndex={0}>
      <Metric title={title} percent={percent} value={value} height={size} isLoading={isLoading} />

      <Tooltip position="top" width={TOOLTIP_WIDTH} textAlign="left" isUsingFocus>
        {emptyReason ?? t(translationKeys.description)}
      </Tooltip>
    </Flex>
  );
};

export const PlanogramMetrics = ({
  pogAtom,
  size,
  isLoading,
}: {
  pogAtom: PrimitiveAtom<Pog | null>;
  size?: "default" | "small" | "big";
  isLoading?: boolean;
}) => {
  const { t } = useTranslation("common");
  const [pog] = useAtom(pogAtom);
  const aesthetics = useMemo(() => pogAestheticsScore(pog), [pog]);
  const expectedSales = useMemo(() => pogExpectedSales(pog), [pog]);

  return (
    <Flex align="center">
      <Grid gap={(size === "small" && "12px") || "20px"} columns={TILE_COLUMNS}>
        <MetricTile
          id="returnOnSpace"
          percent={pog?.calculateReturnOnSpace()}
          size={size}
          isLoading={isLoading}
        />

        <VerticalDivider size={2} />

        <MetricTile
          id="coreRange"
          percent={pog?.calculateCoreRange()}
          size={size}
          isLoading={isLoading}
          emptyReason={
            pog && pog.calculateCoreRange() === null
              ? t(METRIC_TRANSLATION_KEYS.coreRange.emptyReason)
              : null
          }
        />

        <VerticalDivider size={2} />

        <MetricTile
          id="shelfAlignment"
          percent={pog?.calculateShelfAlignment()}
          size={size}
          isLoading={isLoading}
        />

        <VerticalDivider size={2} />

        <MetricTile
          id="dosMos"
          percent={pog?.calculateDosMos()}
          size={size}
          isLoading={isLoading}
        />

        <VerticalDivider size={2} />

        <MetricTile id="aesthetics" percent={aesthetics?.score} size={size} isLoading={isLoading} />

        <VerticalDivider size={2} />

        <MetricTile
          id="expectedSales"
          value={formatExpectedSales(expectedSales)}
          size={size}
          isLoading={isLoading}
          emptyReason={
            expectedSales && expectedSales.isPlaceholderData
              ? t(METRIC_TRANSLATION_KEYS.expectedSales.emptyReason)
              : null
          }
        />
      </Grid>
    </Flex>
  );
};
